import {
    DataTableCustomized,
    createCurrencyFormatter,
    createDateFormatter,
    createOptionsFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { CreateEditProductPriceModalProps } from '@/modals/ProductPriceModals/CreateEdit'
import { getProductPrices } from '@/modules/Settings/services/product-price'
import { ProductPrice } from '@/modules/Settings/types/product-price'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { Plus } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'

export interface ManagePricesModalProps extends ModalProps {
    productId: number
    productName: string
}

const isActive = (price: ProductPrice) => {
    const now = dayjs()
    const from = dayjs(price.start_date)
    const to = price.end_date ? dayjs(price.end_date) : null
    return now.isAfter(from) && (!to || now.isBefore(to))
}

const CURRENCY_LABELS: Record<string, string> = {
    vi: 'VND',
    usd: 'USD',
    eur: 'EUR',
}

const getPriceColumns = (
    t: (key: string) => string
): DataTableColumn<ProductPrice>[] => [
    {
        key: 'product_price_id',
        title: 'ID',
        width: 70,
    },
    {
        key: 'currency',
        title: t('currency'),
        width: 100,
        valueGetter: (row) => CURRENCY_LABELS[row.currency] || row.currency,
    },
    {
        key: 'price',
        title: t('price'),
        width: 150,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'start_date',
        title: t('start-date'),
        width: 130,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'end_date',
        title: t('end-date'),
        width: 130,
        valueGetter: (row) => row.end_date || t('ongoing'),
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'status',
        title: t('status'),
        width: 100,
        valueGetter: (row) => isActive(row),
        formatter: createOptionsFormatter({
            options: [
                { label: t('active'), value: true, color: 'success' },
                { label: t('inactive'), value: false, color: 'error' },
            ],
            renderMode: 'chip',
            textTransform: 'capitalize',
        }),
    },
]

const ManagePricesModal: React.FC<ManagePricesModalProps> = ({
    productId,
    productName,
    onClose,
    open,
}) => {
    const { t } = useTranslation('settings')
    const { setModal } = useModal()

    const {
        data: prices = [],
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.PRODUCT_PRICES, productId],
            queryFn: () => getProductPrices(productId),
        },
        { showLoading: true, showError: true }
    )

    const handleCreate = () => {
        setModal<CreateEditProductPriceModalProps>(
            'ProductPriceModals/CreateEdit/index',
            { productId, productPrice: null }
        )
    }

    const columns = useMemo(() => getPriceColumns(t), [t])

    const stateComponent = renderState()

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={`${t('manage-prices')} - ${productName}`}
            maxWidth="lg"
        >
            <div className="space-y-3">
                <div className="flex justify-end">
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleCreate}
                    >
                        <Plus className="w-4 h-4" />
                        {t('add-price')}
                    </Button>
                </div>
                {stateComponent || (
                    <DataTableCustomized<ProductPrice>
                        columns={columns}
                        rows={prices}
                        rowKey={(row) => row.product_price_id}
                        isFetching={isFetching}
                        emptyMessage={t('no-data')}
                        bordered
                    />
                )}
            </div>
        </Dialog>
    )
}

export default ManagePricesModal
